import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import Marquee from "@/components/Marquee";
import { CtaButton } from "@/components/Bits";
import { CATEGORIES, FEATURED, LEISTUNGEN, BRANDS, CONTACT, HOURS } from "@/data/catalog";

const HERO_IMG =
  "https://images.unsplash.com/photo-1633239953245-bbfdc040a731?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } }),
};

export default function Home() {
  return (
    <>
      <Seo
        title="Waffen · Munition · Zubehör"
        description="IMPEXMILTRADE GMBH in Mureck — Ihr Fachhandel für Waffen, Munition und Zubehör. Beratung für Jagd, Sport, Behörde. Import & Export."
      />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-border" data-testid="hero">
        <div className="blueprint-grid absolute inset-0 opacity-40" aria-hidden />
        <div className="relative mx-auto grid max-w-7xl lg:grid-cols-2">
          <div className="flex flex-col justify-center border-r border-border px-5 py-20 md:py-28">
            <motion.p variants={fade} initial="hidden" animate="show" className="mono-label text-primary">
              Waffenfachhandel · Mureck, AT · seit Jahrzehnten
            </motion.p>
            <motion.h1
              variants={fade}
              initial="hidden"
              animate="show"
              custom={1}
              className="mt-6 font-display text-5xl font-700 uppercase leading-[0.9] tracking-tight sm:text-6xl lg:text-7xl"
            >
              Alles rund
              <br />
              um die <span className="text-primary">Waffe.</span>
            </motion.h1>
            <motion.p
              variants={fade}
              initial="hidden"
              animate="show"
              custom={2}
              className="mt-6 max-w-md font-body text-base text-muted-foreground md:text-lg"
            >
              Waffen, Munition und Zubehör aus einer Hand. Präzise Beratung für Jäger, Sportschützen,
              Behörden und Händler — mit sauberer Abwicklung von Import bis Export.
            </motion.p>
            <motion.div variants={fade} initial="hidden" animate="show" custom={3} className="mt-10">
              <CtaButton to="/kontakt" testid="hero-cta">
                Anfrage stellen <ArrowRight size={16} />
              </CtaButton>
            </motion.div>

            <div className="mt-14 grid max-w-md grid-cols-3 border border-border">
              {[
                { n: "3", l: "Sortimente" },
                { n: "40+", l: "Marken" },
                { n: "AT", l: "Fachhandel" },
              ].map((s, i) => (
                <div key={i} className={`px-4 py-5 ${i < 2 ? "border-r border-border" : ""}`}>
                  <p className="font-display text-3xl font-700 text-foreground">{s.n}</p>
                  <p className="mono-label mt-1 text-muted-foreground">{s.l}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="relative min-h-[320px] overflow-hidden lg:min-h-full">
            <img src={HERO_IMG} alt="Detailaufnahme einer Pistole" className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-background via-background/20 to-transparent" aria-hidden />
            <div className="absolute bottom-5 left-5 border border-border bg-background/80 px-4 py-2 backdrop-blur-sm">
              <p className="mono-label text-primary">REF · MURECK-HQ</p>
            </div>
          </div>
        </div>
      </section>

      <Marquee items={["WAFFEN", "MUNITION", "ZUBEHÖR", "BÜCHSENMACHER", "WAFFENFÜHRERSCHEIN", "JAGD", "SPORT", "BEHÖRDE", "IMPORT", "EXPORT", "BERATUNG"]} />

      {/* KATEGORIEN */}
      <section className="mx-auto max-w-7xl px-5 py-20 md:py-28" data-testid="section-kategorien">
        <div className="flex items-end justify-between border-b border-border pb-6">
          <div>
            <p className="mono-label text-primary">01 / Sortiment</p>
            <h2 className="mt-3 font-display text-3xl font-600 uppercase tracking-tight md:text-4xl">Drei Sektionen</h2>
          </div>
          <span className="mono-label hidden text-muted-foreground sm:block">SEK-01 → SEK-03</span>
        </div>

        <div className="mt-0 grid border-l border-border md:grid-cols-3">
          {CATEGORIES.map((c, i) => (
            <motion.div
              key={c.code}
              variants={fade}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-80px" }}
              custom={i}
            >
              <Link
                to={c.slug}
                data-testid={`category-card-${c.title.toLowerCase()}`}
                className="group flex h-full flex-col border-b border-r border-border transition-colors hover:bg-card"
              >
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={c.image}
                    alt={c.title}
                    className="h-full w-full object-cover grayscale transition-all duration-500 group-hover:grayscale-0 group-hover:scale-105"
                  />
                  <span className="absolute left-3 top-3 mono-label bg-background/80 px-2 py-1 text-primary backdrop-blur-sm">
                    {c.code}
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <div className="flex items-center justify-between">
                    <h3 className="font-display text-2xl font-600 uppercase tracking-tight">{c.title}</h3>
                    <ArrowUpRight className="text-muted-foreground transition-colors group-hover:text-primary" size={20} />
                  </div>
                  <p className="mono-label mt-1 text-primary">{c.tag}</p>
                  <p className="mt-4 font-body text-sm text-muted-foreground">{c.copy}</p>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      <section className="border-y border-border bg-card" data-testid="section-featured">
        <div className="mx-auto grid max-w-7xl lg:grid-cols-2">
          <div className="relative min-h-[300px] overflow-hidden border-r border-border">
            <img src={FEATURED.image} alt={FEATURED.name} className="h-full w-full object-cover" />
            <div className="blueprint-grid absolute inset-0 opacity-30 mix-blend-overlay" aria-hidden />
          </div>
          <div className="p-5 py-16 md:p-14">
            <p className="mono-label text-primary">{FEATURED.code}</p>
            <h2 className="mt-4 font-display text-4xl font-700 uppercase tracking-tight md:text-5xl">{FEATURED.name}</h2>
            <p className="mono-label mt-2 text-muted-foreground">{FEATURED.category}</p>
            <p className="mt-6 max-w-md font-body text-muted-foreground">{FEATURED.copy}</p>

            <div className="mt-10 grid grid-cols-2 border-l border-t border-border">
              {FEATURED.specs.map((s) => (
                <div key={s.k} className="border-b border-r border-border p-5">
                  <p className="mono-label text-muted-foreground">{s.k}</p>
                  <p className="mt-2 font-display text-2xl font-600 text-foreground">{s.v}</p>
                </div>
              ))}
            </div>

            <div className="mt-10">
              <CtaButton to="/waffen" variant="ghost" testid="featured-cta">
                Zum Waffen-Sortiment <ArrowRight size={16} />
              </CtaButton>
            </div>
          </div>
        </div>
      </section>

      {/* LEISTUNGEN */}
      <section className="mx-auto max-w-7xl px-5 py-20 md:py-28" data-testid="section-leistungen">
        <div className="border-b border-border pb-6">
          <p className="mono-label text-primary">02 / Leistungen</p>
          <h2 className="mt-3 font-display text-3xl font-600 uppercase tracking-tight md:text-4xl">
            Was wir für Sie erledigen
          </h2>
        </div>
        <div className="grid border-l border-t border-border md:grid-cols-2">
          {LEISTUNGEN.map((l, i) => (
            <motion.div
              key={l.code}
              variants={fade}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              custom={i}
              className="border-b border-r border-border p-8"
            >
              <div className="flex items-baseline justify-between">
                <span className="mono-label text-primary">{l.code}</span>
              </div>
              <h3 className="mt-4 font-display text-2xl font-500 uppercase tracking-tight">{l.title}</h3>
              <p className="mt-3 font-body text-sm text-muted-foreground">{l.copy}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* MARKEN */}
      <section className="border-t border-border" data-testid="section-marken">
        <div className="mx-auto max-w-7xl px-5 pt-16">
          <p className="mono-label text-muted-foreground">03 / Marken im Programm</p>
        </div>
        <div className="mt-8 overflow-hidden border-y border-border py-6">
          <div className="marquee-track">
            {[...BRANDS, ...BRANDS].map((b, i) => (
              <span key={i} className="mx-8 font-display text-2xl font-500 uppercase tracking-wide text-muted-foreground md:text-3xl">
                {b}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* STANDORT / CTA */}
      <section className="mx-auto max-w-7xl px-5 py-20 md:py-28" data-testid="section-standort">
        <div className="grid border border-border lg:grid-cols-2">
          <div className="border-b border-border p-8 md:p-12 lg:border-b-0 lg:border-r">
            <p className="mono-label text-primary">04 / Standort</p>
            <h2 className="mt-4 font-display text-3xl font-600 uppercase tracking-tight md:text-4xl">Besuchen Sie uns</h2>
            <address className="mt-6 not-italic font-body text-lg text-foreground">
              {CONTACT.street}, {CONTACT.city}
            </address>
            <div className="mt-6 space-y-1">
              <a href={`tel:${CONTACT.phoneHref}`} className="block font-mono-t text-foreground hover:text-primary" data-testid="standort-phone">
                {CONTACT.phone}
              </a>
              <a href={`mailto:${CONTACT.email}`} className="block font-mono-t text-foreground hover:text-primary" data-testid="standort-email">
                {CONTACT.email}
              </a>
            </div>
            <div className="mt-10">
              <CtaButton to="/kontakt" testid="standort-cta">
                Anfrage stellen <ArrowRight size={16} />
              </CtaButton>
            </div>
          </div>
          <div className="p-8 md:p-12">
            <p className="mono-label text-muted-foreground">Öffnungszeiten</p>
            <ul className="mt-6 divide-y divide-border border-y border-border">
              {HOURS.map((h) => (
                <li key={h.day} className="flex flex-col justify-between gap-1 py-4 sm:flex-row sm:items-center">
                  <span className="mono-label text-foreground">{h.day}</span>
                  <span className="font-body text-sm text-muted-foreground">{h.time}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </>
  );
}
